<template>
  <div v-tooltip.placementBottom="tooltip">
    <div
      class="sf-btn sf-btn-icon"
      data-testid="share-workflow-with-pulse-users-button"
      :class="noPulseUsersClass"
      @click="openModal"
    >
      <template v-if="loading">
        <div class="sf-spinner sf-inline-block"></div>
      </template>
      <template v-else-if="isShared && isBlocked">
        <IconPulseShareLocked />
      </template>
      <template v-else-if="isShared">
        <IconPulseShare />
      </template>
      <template v-else>
        <IconNoAccess />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { usePulseShare } from '@app/shared/pinia/share-pulse'
import { computed } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import IconPulseShare from '@app/shared/assets/icons/svg/icon-pulse-share.svg?component'
import IconPulseShareLocked from '@app/shared/assets/icons/svg/icon-pulse-share-locked.svg?component'
import IconNoAccess from '@app/shared/assets/icons/svg/icon-no-access.svg?component'

interface Props {
  ledgerId: number
  objectId: number
  isBlockedInitial?: boolean
  sharedObjectName?: string
}

const I18n = useI18n()

const props = withDefaults(defineProps<Props>(), {
  sharedObjectName: '',
  isBlockedInitial: false,
})

const pulseShareStore = usePulseShare()
pulseShareStore.setCurrentObjectBlocked(props.isBlockedInitial)
pulseShareStore.refreshPulseSharesData(props.ledgerId, props.objectId)

const isShared = computed(() => {
  return pulseShareStore.isShared
})

const isBlocked = computed(() => {
  return pulseShareStore.isCurrentObjectBlocked
})

const loading = computed(() => {
  return !pulseShareStore.requestCompleted
})

const noPulseUsers = computed(() => {
  return pulseShareStore.currentShares.length === 0
})

const tooltip = computed(() => {
  if (noPulseUsers.value) {
    return I18n.t('pulse.sharing.manage_pulse_access_no_users')
  } else if (pulseShareStore.isShared && isBlocked.value) {
    return I18n.t(
      'pulse.sharing.manage_pulse_access_can_access_and_cannot_propose_changes',
      {
        number: pulseShareStore.currentShares.filter((s) => s.shared).length,
        item: props.sharedObjectName,
      }
    )
  } else if (pulseShareStore.isShared) {
    return I18n.t(
      'pulse.sharing.manage_pulse_access_can_access_and_propose_changes',
      {
        number: pulseShareStore.currentShares.filter((s) => s.shared).length,
        item: props.sharedObjectName,
      }
    )
  } else {
    return I18n.t('pulse.sharing.manage_pulse_access_for_workflow_no_shares', {
      workflow_name: props.sharedObjectName,
    })
  }
})

const noPulseUsersClass = computed(() => {
  return noPulseUsers.value ? 'sf-disabled sf-btn-disabled' : ''
})

function openModal() {
  window.modalCitizen.openModalPortal('share-workflow-with-pulse-users-modal')
}
</script>
